import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AttributeStatus } from 'companySettings/enums';
import {
  useGetPublishedPartFinderProductTypes,
  usePartFinderFamilyActions,
} from 'companySettings/hooks';
import { IPartFinderFamily } from 'companySettings/types';
import { Steps } from 'core/components';
import { StepsProvider, useAgGridClientTableContext, useFormContext } from 'core/states';
import { IStep } from 'core/types';
import { Variant } from 'theme/enums';

import { AttributesStep } from './AttributesStep';
import { DetailsStep } from './DetailsStep';
import {
  DetachedAttributes,
  mapAttributesForProductType,
  mapPartFinderFamilyPayload,
} from './mappers';
import { PartsStep } from './PartsStep';
import { IFamilyPartForm, IUpsertPartFinderFamilyFormData } from './types';
import { hasFamilyChanges, prunePartValues } from './utils';

enum FamilyWizardStep {
  ATTRIBUTES = 'attributes',
  DETAILS = 'details',
  PARTS = 'parts',
}

const DETAILS_FIELDS = ['name', 'productTypeId', 'category'];

interface IProps {
  family?: IPartFinderFamily;
  loadedParts: IFamilyPartForm[];
  onClose: () => void;
}

export const UpsertPartFinderFamilySteps = ({ family, loadedParts, onClose }: IProps) => {
  const { t } = useTranslation();

  const { handleSubmit, trigger, getValues, setValue, watch } = useFormContext();

  const hasChanges = hasFamilyChanges(
    watch() as IUpsertPartFinderFamilyFormData,
    family,
    loadedParts
  );
  const { updateRows } = useAgGridClientTableContext();

  const { savePartFinderFamily } = usePartFinderFamilyActions();

  const { records: productTypes = [] } = useGetPublishedPartFinderProductTypes();

  const savedFamilyRef = useRef<IPartFinderFamily | null>(null);

  // The row changes only once the wizard goes: it can sit in the row's own cell or the empty
  // state, and the update re-renders or removes each of them.
  const handleClose = useCallback(() => {
    const savedFamily = savedFamilyRef.current;

    savedFamilyRef.current = null;
    onClose();

    if (savedFamily) {
      updateRows(family ? { update: [savedFamily] } : { add: [savedFamily] }, {
        refreshCells: true,
      });
    }
  }, [family, onClose, updateRows]);

  // The inherited attributes come from the product type's release, again when the product type
  // changed; the family's own ones stay. A family set up in the wizard has nothing saved against
  // the old type's attributes, so they go (the editor asks).
  const takeProductTypeAttributes = useCallback(() => {
    const productTypeId = getValues('productTypeId');

    if (productTypeId === getValues('attributesProductTypeId')) {
      return;
    }

    const productType = productTypes.find(({ id }) => id === productTypeId);
    const { attributes } = mapAttributesForProductType(
      getValues('attributes') ?? [],
      productType,
      DetachedAttributes.DELETE
    );

    setValue('attributes', attributes);
    setValue('parts', prunePartValues(getValues('parts') ?? [], attributes));
    setValue('attributesProductTypeId', productType ? productTypeId : null);
  }, [getValues, productTypes, setValue]);

  const onConfigureAttributes = useCallback(async () => {
    if (!(await trigger(DETAILS_FIELDS, { shouldFocus: true }))) {
      return false;
    }

    takeProductTypeAttributes();
  }, [takeProductTypeAttributes, trigger]);

  // Saved from any step, the wizard closes on the usual delay; it stays open while the form is
  // invalid or the save failed. Either way the steps do not move on.
  const onSave = useCallback(
    (status: AttributeStatus) => async (closeSteps?: () => void) => {
      takeProductTypeAttributes();

      await handleSubmit(async (data: IUpsertPartFinderFamilyFormData) => {
        const { success, partFinderFamily } = await savePartFinderFamily(
          mapPartFinderFamilyPayload(data, family, status)
        );

        if (success) {
          savedFamilyRef.current = partFinderFamily;
        }
      })();

      if (savedFamilyRef.current) {
        setTimeout(() => closeSteps?.(), 1000);
      }

      return false;
    },
    [family, handleSubmit, savePartFinderFamily, takeProductTypeAttributes]
  );

  const onSaveAsDraft = useMemo(() => onSave(AttributeStatus.DRAFT), [onSave]);
  const hasParts = (watch('parts') ?? []).length > 0;

  const steps = useMemo<IStep[]>(
    () => [
      {
        type: FamilyWizardStep.DETAILS,
        title: t('titleCreateNameFamily'),
        content: <DetailsStep />,
        nextButtons: [
          {
            text: t('buttonLabelSaveAsDraft'),
            variant: Variant.SOFT,
            options: { onClick: onSaveAsDraft, disabled: !hasChanges },
          },
          {
            text: t('buttonLabelConfigureTheAttributes'),
            options: { onClick: onConfigureAttributes },
          },
        ],
      },
      {
        type: FamilyWizardStep.ATTRIBUTES,
        title: t('titleConfigureTheAttributes'),
        content: <AttributesStep />,
        nextButtons: [
          {
            text: t('buttonLabelSaveAsDraft'),
            variant: Variant.SOFT,
            options: { onClick: onSaveAsDraft, disabled: !hasChanges },
          },
          { text: t('buttonLabelAddParts') },
        ],
      },
      {
        type: FamilyWizardStep.PARTS,
        title: t('titleAddParts'),
        content: <PartsStep />,
        nextButtons: [
          {
            text: t('buttonLabelSaveAsDraft'),
            variant: Variant.SOFT,
            options: { onClick: onSaveAsDraft, disabled: !hasChanges },
          },
          {
            text: t('buttonLabelSaveAndPublish'),
            // A family needs a part to be published; a saved draft can be published as it is.
            options: { onClick: onSave(AttributeStatus.PUBLISHED), disabled: !hasParts },
          },
        ],
      },
    ],
    [hasChanges, hasParts, onConfigureAttributes, onSave, onSaveAsDraft, t]
  );

  return (
    <StepsProvider steps={steps} onClose={handleClose}>
      <Steps title={t('titleAddPartFinderFamily')} isOpen />
    </StepsProvider>
  );
};
